import { isBogoRule, orderByBogoRule } from "./bogo-preference-order";
/**
 * Buy One Get One selection (ADO #84401): one pool supplies the buying item and another the
 * discounted item. A trigger-item qualifier lends its cart copy and candidate matcher through
 * {@link IBogoCandidateSource}; what the pools ARE is the qualifier's affair — qualifying-item
 * groups for the group-item coupon types, and for the category types (ADO #84406) the coupon's
 * item categories, lent as the same pool for both positions.
 *
 * THE DISCOUNTED ITEM IS CHOSEN FIRST, and the BOGO Rule ranks every order item the discounted pool
 * matches — including the one the buying pool goes on to use when both pools hold the same items.
 * Only then is the buying item taken as the highest priced buying-pool match still going spare.
 * Doing it the other way round (buying item first, rule over the remainder) reads the same for the
 * far commoner coupon whose groups hold different items, but on a coupon whose groups hold the SAME
 * items it shifts every rule down a place: on a 32.98 / 21.03 / 19.20 cart "second highest" lands
 * on 19.20, the cheapest, which is ADO bug #35443 — reported against coupon 1587, the very coupon
 * test case #26295 is written against, and fixed in 2024.
 *
 * The order the bugs settle between them:
 *   #26897 (closed as Designed) — the discount always belongs to a Group 2 item, whatever
 *          Group 1 costs, so Group 2's matches are the only candidates for it.
 *   #35443 — "second highest" on same-item groups means second highest IN THE CART.
 *   #24880 — "highest" on same-item groups means the highest item really is the discounted one.
 *   #59941 — "lowest" means the cheapest Group 2 match, not the cheapest line in the cart.
 *   #80791 — the rule must be deterministic, not a function of qualifying-row order.
 */
export class BogoTriggerItemSelector {
    _source;
    /** Build one per qualification: the source's cart copy is re-cloned on every qualify(). */
    constructor(source) {
        this._source = source;
    }
    /**
     * Fills both Buy One Get One positions, or returns null if they cannot both be filled. Nothing
     * is released on a null result because only the source's cart copy was ever marked.
     *
     * The rule NAMES a preferred discounted item, but a preference that strands the buying position
     * must not sink a coupon that would otherwise apply: a coupon listing Supreme in Group 1 and
     * Supreme + BYO in Group 2, on a cart of one Supreme and one BYO, has an obvious reading under
     * "Discount the highest priced item" — buy the Supreme, discount the BYO — even though the
     * Supreme is the dearest thing Group 2 matches. So the rule's order is walked until a pick
     * leaves the buying pool something to buy with.
     */
    select(coupon, cart, buyingPool, discountedPool) {
        const rule = coupon.bogoSelectionTypeId ?? 0 /* BogoSelectionType.NONE */;
        if (!isBogoRule(rule))
            return null;
        const candidates = this._source.collectCandidates(discountedPool, coupon, cart);
        if (candidates.length === 0)
            return null;
        // Collecting marked them all; each attempt re-marks only the one it is trying, so a
        // rejected pick leaves the cart exactly as it found it.
        for (const candidate of candidates)
            this._release(candidate);
        for (const discounted of this._orderByRule(candidates, rule)) {
            this._mark(discounted);
            const buying = this._selectBuyingItem(buyingPool, coupon, cart);
            if (buying)
                return { buying, discounted };
            this._release(discounted);
        }
        return null;
    }
    /** The dearest buying-pool match still going spare, with the rest released. */
    _selectBuyingItem(pool, coupon, cart) {
        const candidates = this._source.collectCandidates(pool, coupon, cart);
        if (candidates.length === 0)
            return null;
        const [buying] = this._orderByRule(candidates, 3 /* BogoSelectionType.HIGHEST */);
        for (const candidate of candidates) {
            if (candidate !== buying)
                this._release(candidate);
        }
        buying.isBogoBuyingItem = true;
        return buying;
    }
    /** The candidates in the order the rule would have them, best pick first. */
    _orderByRule(candidates, rule) {
        const priced = candidates.map(candidate => ({
            candidate,
            price: this._comparisonPrice(candidate)
        }));
        return orderByBogoRule(priced, rule).map(x => x.candidate);
    }
    /** A half & half or split item is ranked on the price of the whole item, not of one portion. */
    _comparisonPrice(triggerItem) {
        const cartItem = this._source.cartItems[triggerItem.index];
        if (!cartItem)
            return 0;
        return cartItem.halfCount > 1 ? cartItem.halfHalfPrice : cartItem.price;
    }
    /** Marks the cart item behind a candidate as taken — the inverse of _release. */
    _mark(triggerItem) {
        this._adjustMark(triggerItem, 1);
    }
    /** Undoes the mark the source's matcher took, for every portion of a split item. */
    _release(triggerItem) {
        this._adjustMark(triggerItem, -1);
    }
    _adjustMark(triggerItem, delta) {
        const cartItems = this._source.cartItems;
        const cartItem = cartItems[triggerItem.index];
        if (!cartItem)
            return;
        if (cartItem.halfCount > 1) {
            for (const half of cartItems) {
                if (half.itemKey === cartItem.itemKey)
                    half.discountedMarkedQuantity += delta;
            }
            return;
        }
        cartItem.discountedMarkedQuantity += delta;
    }
}
//# sourceMappingURL=bogo-trigger-item-selector.js.map